import React, { useCallback, useEffect, useState } from "react";
import { FiActivity, FiMessageSquare, FiPhoneCall } from "react-icons/fi";
import { getCustomerAiProfile } from "../../../api/analyticsApi";
import { fetchCustomerIssues } from "../../../api/retentionApi";
import IssueCard from "../../Customers/AtRisk/IssueCard";
import { orchestrateAssistant } from "../../../api/assistantApi";
import "./AiTab.css";

/* Customer-card AI panel — the contextualization profile is the single source
   of intelligence here, the same data the revival page renders. The assistant
   answers with quoted, source-cited evidence scoped to this customer. */

function ProfileSection({ customerId }) {
  const [profile, setProfile] = useState(null);
  const [state, setState] = useState("loading"); // loading | profiled | thin_history | not_profiled | error

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!customerId) {
      setState("not_profiled");
      return undefined;
    }
    setState("loading");
    getCustomerAiProfile(customerId)
      .then(({ data }) => {
        if (cancelled) return;
        setProfile(data);
        setState(data?.exists ? data.state : "not_profiled");
      })
      .catch((err) => {
        if (cancelled) return;
        // A missing profile is an honest state, not an error.
        setState(err?.response?.status === 404 ? "not_profiled" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, reloadKey]);

  if (state === "loading") return <div className="gt-ai-profile gt-ai-profile--muted">Loading profile…</div>;
  if (state === "error") {
    return (
      <div className="gt-ai-profile gt-ai-profile--muted">
        Couldn’t load the profile —{" "}
        <button type="button" className="gt-ai-retry" onClick={() => setReloadKey((k) => k + 1)}>
          try again
        </button>
      </div>
    );
  }
  if (state === "not_profiled") {
    return (
      <div className="gt-ai-profile gt-ai-profile--muted">
        Not yet contextualized. A profile is built automatically from this customer’s history.
      </div>
    );
  }

  const health = profile?.relationship_health || {};
  const complaints = Array.isArray(profile?.unresolved_complaints) ? profile.unresolved_complaints : [];
  const citations = Array.isArray(profile?.evidence_citations) ? profile.evidence_citations : [];
  const refreshed = profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString() : null;

  return (
    <div className="gt-ai-profile">
      {state === "thin_history" && (
        <div className="gt-ai-thin">
          Thin history — {citations.length} recorded {citations.length === 1 ? "event" : "events"}, low-confidence read.
        </div>
      )}
      {health.summary && (
        <div className="gt-ai-health">
          <span className={`gt-ai-health-dot gt-ai-health-dot--${(health.trajectory || "stable").toLowerCase()}`} />
          {health.summary}
        </div>
      )}
      <p className="gt-ai-profile-meta">
        {[
          profile.lifecycle_stage ? `A ${profile.lifecycle_stage.replace(/_/g, " ")} customer` : "",
          typeof profile.churn_risk_score === "number" && profile.churn_risk_score > 0
            ? `${profile.churn_risk_score >= 0.66 ? "likely" : profile.churn_risk_score >= 0.33 ? "possibly" : "unlikely"} to leave (${Math.round(profile.churn_risk_score * 100)}% risk)`
            : "",
          typeof profile.confidence === "number" ? `read with ${profile.confidence >= 0.7 ? "good" : profile.confidence >= 0.4 ? "fair" : "low"} confidence` : "",
        ].filter(Boolean).join(", ")}
        {refreshed ? `. Last read ${refreshed}${profile.is_stale ? "; a refresh is pending" : ""}.` : "."}
      </p>
      {complaints.length > 0 && (
        <div className="gt-ai-complaints">
          {complaints.slice(0, 3).map((c, i) => (
            <span key={i} className="gt-ai-complaint-chip">
              {c.topic || c.summary}
            </span>
          ))}
        </div>
      )}
      {citations.length > 0 && (
        <ul className="gt-ai-citations">
          {citations.slice(0, 4).map((c, i) => (
            <li key={i}>
              “{c.claim}”
              <span className="gt-ai-citation-src">
                {" — "}
                {c.source_label || c.source_type || "record"}
                {c.date ? `, ${c.date}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      {profile.campaign_status && (
        <div className="gt-ai-campaign">
          In win-back campaign ({profile.campaign_status.proposal_status.replace(/_/g, " ")})
          {profile.campaign_status.outcomes?.length > 0 &&
            ` · ${profile.campaign_status.outcomes.join(", ").replace(/_/g, " ")}`}
        </div>
      )}
    </div>
  );
}

const CHAT_STORE_PREFIX = "abon-ai-chat:";
const CHAT_STORE_LIMIT = 30;

function loadThread(customerId) {
  if (!customerId) return [];
  try {
    return JSON.parse(localStorage.getItem(`${CHAT_STORE_PREFIX}${customerId}`)) || [];
  } catch {
    return [];
  }
}

function saveThread(customerId, messages) {
  if (!customerId) return;
  try {
    localStorage.setItem(
      `${CHAT_STORE_PREFIX}${customerId}`,
      JSON.stringify(messages.slice(-CHAT_STORE_LIMIT)),
    );
  } catch {
    /* storage full/unavailable — thread stays session-only */
  }
}

function IssuesSection({ customerId, onAction }) {
  const [issues, setIssues] = useState(undefined); // undefined = loading, null = no route
  useEffect(() => {
    let alive = true;
    fetchCustomerIssues(customerId).then((rows) => { if (alive) setIssues(rows); }).catch(() => { if (alive) setIssues(null); });
    return () => { alive = false; };
  }, [customerId]);
  if (issues === null || issues === undefined) return null; // C's route is not on this API yet, or still loading
  const open = issues.filter((i) => i.status !== "resolved");
  return (
    <div className="gt-ai-issues">
      <div className="gt-ai-issues__head">Open issues{open.length ? ` · ${open.length}` : ""}</div>
      {open.length ? open.map((i) => <IssueCard key={i.id} issue={i} onAction={onAction} />) : <p className="gt-ai-profile gt-ai-profile--muted">Nothing open on this account.</p>}
    </div>
  );
}

export default function AiTab({ lead, aiInput, setAiInput, chatRef, onIssueAction }) {
  const [busy, setBusy] = useState(false);

  const raw = lead?.raw || {};
  const objectType = String(lead?.object || raw?.object || "").trim().toLowerCase();
  const customerId =
    lead?.customer_id || raw?.customer_id || (objectType === "customer" ? lead?.id || raw?.id : "");
  const leadId = objectType === "lead" ? lead?.lead_id || lead?.id || raw?.lead_id || raw?.id : "";

  // The thread is keyed to the CUSTOMER, not the popup instance — closing the
  // card must never lose the conversation. Persisted locally per customer.
  const [aiMessages, setAiMessages] = useState(() => loadThread(customerId));

  useEffect(() => {
    setAiMessages(loadThread(customerId));
  }, [customerId]);

  useEffect(() => {
    saveThread(customerId, aiMessages);
    if (chatRef?.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [aiMessages, customerId, chatRef]);

  const sendMessage = useCallback(
    async (text) => {
      const userInput = String(text || "").trim();
      if (!userInput || busy) return;
      setAiInput("");
      setBusy(true);
      setAiMessages((prev) => [...prev, { sender: "user", text: userInput }]);
      try {
        const conversation = aiMessages
          .filter((m) => m.sender === "user" || m.sender === "ai")
          .slice(-6)
          .map((m) => ({ role: m.sender === "user" ? "user" : "assistant", content: m.text }));
        const data = await orchestrateAssistant({
          message: userInput,
          conversation,
          page_context: {
            page: "customers",
            path: customerId ? `/customers/${customerId}` : "/customers",
            entity_type: customerId ? "customer" : "lead",
            customer_id: customerId || undefined,
            lead_id: !customerId && leadId ? leadId : undefined,
          },
        });
        const answer = String(data?.answer || "").trim();
        setAiMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            text: answer || "No answer available for that question yet.",
            citations: Array.isArray(data?.citations)
              ? data.citations
                  .filter((c) => c?.snippet && !["recent_context", "tenant_ai_context"].includes(c.kind))
                  .slice(0, 4)
              : [],
          },
        ]);
      } catch (err) {
        const timedOut = err?.code === "ECONNABORTED";
        setAiMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            text: timedOut
              ? "Couldn’t reach the AI in time — try again."
              : "Couldn’t reach the AI — try again.",
          },
        ]);
      } finally {
        setBusy(false);
      }
    },
    [aiMessages, busy, customerId, leadId, setAiInput, setAiMessages],
  );

  /* Every button routes through the same cited assistant — no dead buttons.
     "Generate Contract Draft" was removed: no executor produces contracts. */
  const actions = [
    {
      label: "Summarize Profile",
      cmd: "Summarize this customer's relationship: health, risks, unresolved issues, and recommended next steps. Cite the evidence.",
      icon: <FiActivity size={16} />,
    },
    {
      label: "Follow-Up Plan",
      cmd: "Suggest a follow-up plan for this customer based on their history and current risk. Cite the evidence behind each step.",
      icon: <FiPhoneCall size={16} />,
    },
    {
      label: "Draft a Message",
      cmd: "Draft a short, personalized SMS to this customer that acknowledges their history. Nothing sends — this is a draft for review.",
      icon: <FiMessageSquare size={16} />,
    },
  ];

  return (
    <div className="gt-ai">
      <div className="gt-ai-header">
        <div className="gt-ai-orb" />
        <div className="gt-ai-header-text">
          <h3>Customer Intelligence</h3>
          <p className="gt-ai-sub">What we know about this customer, with the records to back it up.</p>
        </div>
      </div>

      <IssuesSection customerId={customerId} onAction={onIssueAction} />
      <ProfileSection customerId={customerId} />

      <div className="gt-ai-actions">
        {actions.map((action, idx) => (
          <button key={idx} className="gt-ai-action-btn" onClick={() => sendMessage(action.cmd)} disabled={busy}>
            <span className="gt-ai-action-icon">{action.icon}</span>
            <span>{action.label}</span>
          </button>
        ))}
      </div>

      <div className="gt-ai-chat" ref={chatRef}>
        {aiMessages.length === 0 && (
          <div className="gt-ai-empty">
            Ask anything about this customer — “Why are they at risk?” “When did they last complain?”
            Answers cite the records they come from.
          </div>
        )}
        {aiMessages.map((msg, i) => (
          <div key={i} className={`gt-ai-msg ${msg.sender}`}>
            <div className="gt-ai-bubble">
              {msg.text}
              {Array.isArray(msg.citations) && msg.citations.length > 0 && (
                <ul className="gt-ai-msg-citations">
                  {msg.citations.map((c, j) => (
                    <li key={j}>
                      <strong>{c.title}</strong>: {c.snippet}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="gt-ai-msg ai"><div className="gt-ai-bubble">Thinking…</div></div>}
      </div>

      <div className="gt-ai-input-row">
        <input
          type="text"
          className="gt-ai-input"
          value={aiInput}
          placeholder="Ask about this customer…"
          onChange={(e) => setAiInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage(aiInput)}
          disabled={busy}
        />
        <button className="gt-ai-send" onClick={() => sendMessage(aiInput)} disabled={busy}>
          Send
        </button>
      </div>
    </div>
  );
}
